import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthorizationService } from '../services/authorization/authorization.service';
import { AuthorizationStateService } from '../services/authorization/authorization-state.service';
import { PropertiesService } from '../services/property.service';
import { MemberService } from '../services/member.service';
import { OrganizationService } from '../services/organization.service';
import { UserService } from '../services/user.service';
import { SystemPageRoutes } from '../constants/constants';

export enum SoftDeleteEntityType {
  Property = 'property',
  Member = 'member',
  Organization = 'organization',
  User = 'user'
}

export interface SoftDeleteGuardConfig {
  paramKey: string;
  entityType: SoftDeleteEntityType;
}

export const softDeleteGuard: CanActivateFn = async (route, _state) => {
  const config = route.data['softDeleteCheck'] as SoftDeleteGuardConfig | undefined;
  if (!config) return true;

  const authorizationService = inject(AuthorizationService);
  const authorizationStateService = inject(AuthorizationStateService);
  const router = inject(Router);

  // Staff can always view deleted entities — no API call needed
  const authState = authorizationStateService.authorizationState();
  if (authorizationService.isStaff(authState ?? null)) {
    return true;
  }

  // Non-staff: fetch entity and check status
  const entityId = Number(route.paramMap.get(config.paramKey));
  if (!entityId || isNaN(entityId)) {
    return router.createUrlTree(SystemPageRoutes.NOT_FOUND());
  }

  let response: { success?: boolean; data?: { status?: string } } | undefined;
  switch (config.entityType) {
    case SoftDeleteEntityType.Property:
      response = await inject(PropertiesService).getPropertyById(entityId);
      break;
    case SoftDeleteEntityType.Member:
      response = await inject(MemberService).getMemberById(entityId);
      break;
    case SoftDeleteEntityType.Organization:
      response = await inject(OrganizationService).getOrganizationById(entityId);
      break;
    case SoftDeleteEntityType.User:
      response = await inject(UserService).getUserById(entityId);
      break;
  }

  // Fail closed: if the service call failed or entity not found, block access
  if (!response?.success || !response?.data) {
    return router.createUrlTree(SystemPageRoutes.NOT_FOUND());
  }

  if (response.data.status === 'Deleted') {
    return router.createUrlTree(SystemPageRoutes.NOT_FOUND());
  }

  return true;
};
